import { formatSn } from '@oskecare/fe-utilities/scripts/format';
import Love from './Models/Love.js';
import LoveBenefit from './Models/LoveBenefit.js';
import LoveGroup from './Models/LoveGroup.js';
import LoveMember from './Models/LoveMember.js';
import { BENEFITS, BENEFITS_NEW, CONVERGENT_GROUP_FLAG_BASIC } from './constants.js';

const LOG_SOURCE = 'infra-love';

/**
 * Infrastructure module for providing love related data.
 *
 * @property {CustomerDataManager} _customerDataManager
 * @property {Products} products
 * @property {Logger} _logger
 */
export default class LoveManager {
  /**
   * @param {CustomerDataManager} customerDataManager
   * @param {Products} products
   * @param {Logger} logger
   */
  constructor({ customerDataManager, products, logger }) {
    this._customerDataManager = customerDataManager;
    this._products = products;
    this._logger = logger;
  }

  /**
   * @param {(Characteristic|{name: string, value: string})[]} characteristics
   * @param {string} characteristicName
   * @returns {string|{characteristic: {name: string, value: string}[]}}
   */
  static getCharacteristicValueByName(characteristics, characteristicName) {
    const foundCharacteristics = characteristics.filter(
      characteristic => characteristic.name === characteristicName,
    );

    if (!foundCharacteristics.length) {
      throw new Error(`Characteristics "${characteristicName}" not found.`);
    }

    if (foundCharacteristics.length > 1) {
      throw new Error(
        `Unexpected number of characteristic "${characteristicName}" found. Expected 1, found "${foundCharacteristics.length}"`,
      );
    }

    return foundCharacteristics[0].value;
  }

  /**
   * Returns all love groups, new and old, customer has except love groups of type B,
   * which represents love basic. Love basic is only eligibility for old love, not actual love group itself.
   *
   * @param {boolean} isNonessential
   * @return {Promise<?(Love[])>}
   */
  async getLoveGroups(isNonessential = false) {
    const loveGroups = await this._products.inventory.getLove(isNonessential);
    if (!loveGroups.hasItems) {
      return null;
    }

    const groupMembers = loveGroups.items
      .map(item =>
        item.characteristics.filter(characteristic => characteristic.name === 'groupMember'),
      )
      .flat();

    const groupMemberSns = groupMembers.map(eligibleSubscription =>
      LoveManager.getCharacteristicValueByName(eligibleSubscription.value.characteristic, 'MSISDN'),
    );

    const customerData = await this._customerDataManager.getCustomerData();

    const productData = await Promise.all(
      customerData.snDataList
        .filter(item => groupMemberSns.includes(formatSn(item.sn, false)))
        .map(async snListItem => ({
          sn: snListItem.sn,
          tariffName: (await this._products.inventory.getBasicProduct(snListItem.sn)).singleItem
            .name,
        })),
    );

    const love17GrowEligibleMembers = await this.getLoveEligibleMembers();

    const groups = loveGroups.items
      .map(loveData => {
        const groupDetail = LoveManager.getCharacteristicValueByName(
          loveData.characteristics,
          'groupDetail',
        );

        return new LoveGroup({
          status: loveData.status,
          type: LoveManager.getCharacteristicValueByName(groupDetail.characteristic, 'typeId'),
          flag: LoveManager.getCharacteristicValueByName(groupDetail.characteristic, 'flag'),
          benefits: loveData.getCharacteristicsByName('groupBenefit').map(
            benefitData =>
              new LoveBenefit({
                id: LoveManager.getCharacteristicValueByName(
                  benefitData.value.characteristic,
                  'id',
                ),
                status: LoveManager.getCharacteristicValueByName(
                  benefitData.value.characteristic,
                  'status',
                ),
              }),
          ),
          members: loveData.getCharacteristicsByName('groupMember').map(memberData => {
            const subscriptionId = LoveManager.getCharacteristicValueByName(
              memberData.value.characteristic,
              'userId',
            );
            const sn = LoveManager.getCharacteristicValueByName(
              memberData.value.characteristic,
              'MSISDN',
            );

            const { isFixed, nickname } = LoveManager.getIsFixedAndNickname(
              customerData,
              subscriptionId,
            );

            return new LoveMember({
              sn,
              subscriptionId,
              isFixed,
              nickname,
              tariffName:
                productData.find(product => formatSn(product.sn, false) === formatSn(sn, false))
                  ?.tariffName ?? '',
              isLove17GrowEligible: love17GrowEligibleMembers.some(
                eligibleMember => eligibleMember.subscriptionId === subscriptionId,
              ),
            });
          }),
        });
      })
      .filter(group => group.flag !== CONVERGENT_GROUP_FLAG_BASIC);

    if (groups.length === 0) {
      return null;
    }
    const love = new Love({ groups });
    this.logLoveSpecificErrors(love);
    return love;
  }

  /**
   * Returns all current and potential members of Love under CN.
   *
   * @param {boolean} isNonessential
   * @return {Promise<LoveMember[]>}
   */
  async getLoveEligibleMembers(isNonessential = false) {
    const loveProductOffering = await this._products.offering.getLove(isNonessential);
    if (!loveProductOffering.hasItems) {
      return [];
    }
    const eligibleSubscriptions = loveProductOffering.singleItem.characteristics.filter(
      characteristic => characteristic.name === 'eligibleSubscription',
    );

    const eligibleSns = eligibleSubscriptions.map(eligibleSubscription =>
      LoveManager.getCharacteristicValueByName(
        eligibleSubscription.value.characteristic,
        'MISISDN',
      ),
    );

    const customerData = await this._customerDataManager.getCustomerData();

    const productData = await Promise.all(
      customerData.snDataList
        .filter(item => eligibleSns.includes(item.sn))
        .map(async snDataItem => ({
          sn: snDataItem.sn,
          tariffName: (await this._products.inventory.getBasicProduct(snDataItem.sn)).singleItem
            .name,
        })),
    );

    return eligibleSubscriptions.map(eligibleSubscription => {
      const subscriptionId = LoveManager.getCharacteristicValueByName(
        eligibleSubscription.value.characteristic,
        'id',
      );
      const sn = LoveManager.getCharacteristicValueByName(
        eligibleSubscription.value.characteristic,
        'MISISDN',
      );

      const { isFixed, nickname } = LoveManager.getIsFixedAndNickname(customerData, subscriptionId);

      return new LoveMember({
        sn,
        subscriptionId,
        isFixed,
        nickname,
        tariffName:
          productData.find(product => formatSn(product.sn, false) === formatSn(sn, false))
            ?.tariffName ?? '',
        isLove17GrowEligible: true,
      });
    });
  }

  /**
   * @param {CustomerData} customerData
   * @param {string} subscriptionId
   * @return {{isFixed: boolean, nickname: string}}
   */
  static getIsFixedAndNickname(customerData, subscriptionId) {
    if (!customerData.hasSnDataBySubscriptionId(subscriptionId)) {
      return { isFixed: false, nickname: '' };
    }
    const snData = customerData.getSnDataBySubscriptionId(subscriptionId);

    // TODO: unify `isFixed` definitions
    return { isFixed: snData.category !== 'TELE/FAX/DATA', nickname: snData.nickname };
  }

  /**
   * @param {Love} love
   */
  logLoveSpecificErrors(love) {
    love.love17GrowActiveGroups.forEach(group => {
      if (group.hasObtainedAndExpectedBenefitsDiscrepancy) {
        this._logger.error(
          `Unexpected benefits obtained for love group with members "${group.currentMemberSns.toString()}". Expected benefits: "${BENEFITS.get(group.currentMembers.find(member => member.isGrowVoice).expectedGrowBenefitLevel)}" or "${BENEFITS_NEW.get(group.currentMembers.find(member => member.isGrowVoice).expectedGrowBenefitLevel)}". Obtained benefits: "${group.appliedBenefits.map(benefit => benefit.id)}"`,
          LOG_SOURCE,
        );
      }
    });
    love.groups.forEach(group => {
      if (group.isInProtectionPeriod) {
        this._logger.warning(
          `Group with members "${group.currentMemberSns.toString()}" is in PROTECTION_PERIOD.`,
          LOG_SOURCE,
        );
      }
    });
  }
}
